import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Package, Clock, MapPin } from 'lucide-react';
import { OrderTimeline } from '../components/OrderTimeline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getOrderTypeLabel = (orderType, quantity) => {
  switch (orderType) {
    case 'stems':
      return `${quantity} ${quantity === 1 ? 'stem' : 'stems'}`;
    case 'subscription-weekly':
      return 'Weekly Subscription';
    case 'subscription-monthly':
      return 'Monthly Subscription';
    default:
      return `${quantity} stems`;
  }
};

export const TrackOrder = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialId = searchParams.get('orderId') || '';
  const [orderId, setOrderId] = useState(initialId);
  const [order, setOrder] = useState(null);
  const [productsMap, setProductsMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/products`).then(res => {
      const map = {};
      res.data.forEach(p => { map[p.id] = p; });
      setProductsMap(map);
    }).catch(() => {});
  }, []);

  const fetchOrder = async (id) => {
    const cleaned = id.trim().replace(/^#/, '').trim();
    if (!cleaned) return;
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      const res = await axios.get(`${API}/orders/${encodeURIComponent(cleaned)}`);
      setOrder(res.data);
      setSearchParams({ orderId: cleaned });
    } catch (err) {
      if (err?.response?.status === 404) {
        setError('No order found with that ID. Please check your confirmation email and try again.');
      } else {
        setError('Could not load order. Please try again in a moment.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialId) {
      fetchOrder(initialId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchOrder(orderId);
  };

  const shortId = order?.id?.substring(0, 8).toUpperCase();

  return (
    <div className="min-h-screen bg-brand-cream pt-24 pb-16 px-6 fade-in" data-testid="track-order-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <p className="uppercase tracking-[0.3em] text-xs font-bold text-brand-gold mb-4">
            Order Tracking
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-brand-green-dark mb-4">
            Where are my lilies?
          </h1>
          <p className="font-body text-base text-brand-green-dark/70 max-w-xl mx-auto">
            Enter your Order ID (found in your confirmation email) to see the current status of your delivery.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-brand-blush/30 gold-border-glow p-6 sm:p-8 rounded-sm mb-8" data-testid="track-form">
          <label className="block font-body text-sm text-brand-green-dark mb-2" htmlFor="orderId">
            Order ID
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              id="orderId"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="Paste short ID (e.g. 2F161579) or full Order ID"
              className="flex-1 px-4 py-3 border border-brand-gold/40 bg-brand-cream rounded-sm focus:outline-none focus:border-brand-gold smooth-transition font-body text-brand-green-dark"
              data-testid="track-order-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-brand-green text-brand-cream px-8 py-3 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-gold hover:text-brand-green-dark smooth-transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="track-order-submit"
            >
              <Search className="w-4 h-4" strokeWidth={1.5} />
              {loading ? 'Loading...' : 'Track'}
            </button>
          </div>
          {error && (
            <p className="mt-4 font-body text-sm text-red-700" data-testid="track-order-error">
              {error}
            </p>
          )}
        </form>

        {order && (
          <div data-testid="track-order-result" className="space-y-8">
            {/* Order info */}
            <div className="bg-brand-blush/25 gold-border-glow p-6 rounded-sm flex flex-col sm:flex-row justify-between gap-4">
              <div>
                <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-1">Order ID</p>
                <p className="font-heading text-xl text-brand-green-dark">#{shortId}</p>
              </div>
              <div>
                <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-1">Total</p>
                <p className="font-heading text-xl text-brand-green-dark">₹{order.totalAmount}</p>
              </div>
              <div>
                <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-1">Current Status</p>
                <p className="font-heading text-xl text-brand-green">{order.status || 'Order Received'}</p>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-brand-cream gold-border-glow p-8 rounded-sm">
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-6">
                Delivery Progress
              </p>
              <OrderTimeline status={order.status || 'Order Received'} />
            </div>

            {/* Items */}
            <div className="bg-brand-cream gold-border-glow p-8 rounded-sm">
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">
                Items Ordered
              </p>
              <div className="space-y-3">
                {order.items?.map((item, idx) => {
                  const product = productsMap[item.productId] || {};
                  return (
                    <div key={`${item.productId}-${item.orderType}-${idx}`} className="flex justify-between border-b border-brand-gold/20 pb-3 last:border-b-0">
                      <div>
                        <p className="font-heading text-lg text-brand-green-dark">{product.name || 'Oriental Lily'}</p>
                        <p className="font-body text-xs text-brand-gold uppercase tracking-wider mt-1">
                          {getOrderTypeLabel(item.orderType, item.quantity)}
                        </p>
                      </div>
                      <p className="font-body text-brand-green-dark">₹{item.price * item.quantity}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Delivery details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-brand-sage/20 p-6 rounded-sm gold-border-glow">
                <Clock className="w-6 h-6 text-brand-gold mb-2" strokeWidth={1.5} />
                <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-2">Delivery Window</p>
                <p className="font-body text-sm text-brand-green-dark">Within 2 business days</p>
              </div>
              <div className="bg-brand-blush/30 p-6 rounded-sm gold-border-glow">
                <MapPin className="w-6 h-6 text-brand-gold mb-2" strokeWidth={1.5} />
                <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-2">Delivering To</p>
                <p className="font-body text-sm text-brand-green-dark leading-relaxed">
                  {order.customerName}<br />
                  {order.address}<br />
                  {order.city}, {order.pincode}
                </p>
              </div>
            </div>
          </div>
        )}

        {!order && !loading && !error && (
          <div className="text-center py-12" data-testid="track-order-hint">
            <Package className="w-12 h-12 text-brand-gold/40 mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-body text-brand-green-dark/60">
              Enter your Order ID above to see your delivery status.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
